import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Input, Modal, Popconfirm, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { CopyOutlined, PlusOutlined } from '@ant-design/icons';
import { adminUsersProxy } from '../services/catalogServiceAPI/adminUsersProxy';
import { useCurrentUser } from '../../user/permissions';
import { ScopesEditor } from './ScopesEditor';
import { describeGranted, toGranted, toSelection } from './scopeSelection';

const { Title, Text } = Typography;

const STAFF_DOMAIN = '@designwithfriends.com';
const backOfficeUrl = () => `${window.location.origin}${process.env.PUBLIC_URL || ''}`;

const copyBackOfficeLink = async () => {
    await navigator.clipboard.writeText(backOfficeUrl());
    message.success('Back-office link copied');
};

/** `user` is the row being edited, or null to invite someone new. */
const UserDialog = ({ user, onClose, onSaved }) => {
    const [email, setEmail] = useState('');
    const [selection, setSelection] = useState(() => toSelection(user?.scopes));
    const [saving, setSaving] = useState(false);

    const scopes = toGranted(selection);
    const fullEmail = email.trim().toLowerCase() + (email.includes('@') ? '' : STAFF_DOMAIN);
    const emailValid = !!user || /^[^@\s]+@designwithfriends\.com$/.test(fullEmail);

    const save = async () => {
        setSaving(true);
        try {
            if (user) {
                await adminUsersProxy.update(user.email, scopes);
                message.success(`${user.email} updated. It applies the next time they load the back-office.`);
            } else {
                await adminUsersProxy.invite(fullEmail, scopes);
                message.success(`${fullEmail} added. Send them the back-office link to sign in with Google.`);
            }
            onSaved();
        } catch (e) {
            message.error(e.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open
            width={680}
            title={user ? `Permissions for ${user.email}` : 'Invite a user'}
            okText={user ? 'Save' : 'Invite'}
            okButtonProps={{ disabled: !emailValid || scopes.length === 0 }}
            confirmLoading={saving}
            onOk={save}
            onCancel={onClose}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {!user && (
                    <div>
                        <Input
                            autoFocus
                            placeholder="name"
                            addonAfter={email.includes('@') ? undefined : STAFF_DOMAIN}
                            value={email}
                            status={email && !emailValid ? 'error' : undefined}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <Text type="secondary">
                            Only {STAFF_DOMAIN} Google accounts can sign in. Access starts on their first sign-in.
                        </Text>
                    </div>
                )}
                <ScopesEditor selection={selection} onChange={setSelection} />
            </Space>
        </Modal>
    );
};

export const AdminUsers = observer(() => {
    const currentUserStore = useCurrentUser();
    const myEmail = (currentUserStore.user?.email || '').toLowerCase();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // undefined: closed, null: inviting, a row: editing it.
    const [dialogUser, setDialogUser] = useState(undefined);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setUsers(await adminUsersProxy.list());
            setError(null);
        } catch (e) {
            setError(e.message || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const remove = async (email) => {
        try {
            await adminUsersProxy.remove(email);
            message.success(`${email} removed`);
            load();
        } catch (e) {
            message.error(e.message || 'Failed to remove');
        }
    };

    const columns = [
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (email) => <Space>{email}{email === myEmail && <Tag>you</Tag>}</Space>,
        },
        {
            title: 'Permissions',
            dataIndex: 'scopes',
            key: 'scopes',
            render: (scopes) => (
                <Space size={[4, 4]} wrap>
                    {scopes.map((scope) => {
                        const { label, description } = describeGranted(scope);
                        return (
                            <Tooltip key={scope} title={description}>
                                <Tag color={scope === '*' ? 'purple' : undefined}>{label}</Tag>
                            </Tooltip>
                        );
                    })}
                </Space>
            ),
        },
        {
            title: 'Last sign-in',
            dataIndex: 'lastLoginAt',
            key: 'lastLoginAt',
            render: (value) => (value ? new Date(value).toLocaleString() : <Tag color="orange">Invited, not signed in yet</Tag>),
        },
        { title: 'Invited by', dataIndex: 'invitedByEmail', key: 'invitedByEmail', render: (value) => value || '—' },
        {
            title: '',
            key: 'actions',
            align: 'right',
            render: (_, user) => user.email !== myEmail && (
                <Space>
                    <Button size="small" onClick={() => setDialogUser(user)}>Edit</Button>
                    <Popconfirm
                        title={`Remove ${user.email}?`}
                        description="They lose all back-office access."
                        okText="Remove"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => remove(user.email)}
                    >
                        <Button size="small" danger>Remove</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                <Title level={3} style={{ margin: 0 }}>Users</Title>
                <Space>
                    <Button icon={<CopyOutlined />} onClick={copyBackOfficeLink}>Copy back-office link</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setDialogUser(null)}>Invite user</Button>
                </Space>
            </Space>

            {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} showIcon />}

            <Table rowKey="email" loading={loading} columns={columns} dataSource={users} pagination={false} />

            {dialogUser !== undefined && (
                <UserDialog
                    key={dialogUser?.email ?? 'invite'}
                    user={dialogUser}
                    onClose={() => setDialogUser(undefined)}
                    onSaved={() => { setDialogUser(undefined); load(); }}
                />
            )}
        </div>
    );
});
